/**
 * Descriptors come from JSON/API; `required` may not be a real boolean (e.g. `"false"` is truthy in JS).
 * Only explicit affirmative values count as required.
 */
export function isExtensionFieldRequired(descriptor: { required?: unknown }): boolean {
  const r = descriptor?.required;
  if (r === true) return true;
  if (r === 1) return true;
  if (typeof r === "string") {
    const t = r.trim().toLowerCase();
    return t === "true" || t === "1" || t === "yes";
  }
  return false;
}

export function hasSignUpExtensionValue(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === "boolean") return true;
  if (typeof value === "string") {
    const t = value.trim();
    if (!t) return false;
    if (t.startsWith("{")) {
      try {
        const parsed = JSON.parse(t) as { complete?: boolean; value?: Record<string, unknown> };
        if (parsed?.value && typeof parsed.value === "object") {
          return Object.values(parsed.value).some(v => String(v ?? "").trim() !== "");
        }
        return Boolean(parsed?.complete);
      } catch {
        return true;
      }
    }
    return true;
  }
  return true;
}

export function isSignUpExtensionEditableAfterSignup(descriptor: { disableEditAfterSignup?: unknown }): boolean {
  const v = descriptor?.disableEditAfterSignup;
  if (v === false) return true;
  if (v === 0) return true;
  if (v === undefined) return true;
  if (typeof v === "string") {
    const t = v.trim().toLowerCase();
    if (t === "false" || t === "0" || t === "no") return true;
  }
  if (v === true) return false;
  if (v === 1) return false;
  if (typeof v === "string") {
    const t = v.trim().toLowerCase();
    if (t === "true" || t === "1" || t === "yes") return false;
  }
  return false;
}

/**
 * A descriptor may declare `metadata.showWhen: { field, equals }` to render only while another
 * field in the same extension set holds a given value (e.g. a billing address that appears once
 * "differs from the entity address" is ticked). Hidden fields never render and are never required.
 * `equals` defaults to `true` so a plain checkbox controller needs no value.
 */
export function isSignUpFieldVisible(
  descriptor: { metadata?: Record<string, unknown> },
  extensions: Record<string, unknown> | null | undefined
): boolean {
  const showWhen = descriptor?.metadata?.showWhen as { field?: unknown; equals?: unknown } | undefined;
  if (!showWhen || typeof showWhen !== "object" || typeof showWhen.field !== "string" || !showWhen.field) {
    return true;
  }
  const actual = extensions?.[showWhen.field];
  const expected = showWhen.equals === undefined ? true : showWhen.equals;
  // Checkbox values arrive as booleans; everything else compares as a string.
  if (typeof expected === "boolean") return Boolean(actual) === expected;
  return String(actual ?? "") === String(expected);
}
